import React, { useEffect, useId, useRef, useSyncExternalStore } from "react";
import {
  getAlertSoundSnapshot,
  reloadFailedAlertSounds,
  retryBlockedAlertSounds,
  subscribeAlertSound,
} from "../../Utils/alertSoundGuard";
import { useAnnounce } from "../LiveAnnouncer/LiveAnnouncer";
import { useLostFocusReturn } from "../../Hooks/useLostFocusReturn";
import "./alertsoundbanner.css";

const ALERT_SOUND_ON_ANNOUNCEMENT = "Alert sound turned on.";

const enclosingDialog = (element) =>
  element.closest('[role="dialog"], [role="alertdialog"]');
const dialogTargets = (dialog) => (dialog ? [dialog] : []);

function AlertSoundBanner() {
  const { blocked, loadFailed } = useSyncExternalStore(
    subscribeAlertSound,
    getAlertSoundSnapshot,
  );
  const announce = useAnnounce();
  const bannerRef = useRef(null);
  const messageId = useId();
  const silenced = blocked || loadFailed;
  const wasSilencedRef = useRef(silenced);

  useLostFocusReturn(bannerRef, {
    keyOf: enclosingDialog,
    targetsFor: dialogTargets,
  });

  useEffect(() => {
    const wasSilenced = wasSilencedRef.current;
    wasSilencedRef.current = silenced;
    if (wasSilenced && !silenced) {
      announce(ALERT_SOUND_ON_ANNOUNCEMENT);
    }
  }, [silenced]);

  if (loadFailed) {
    return (
      <div ref={bannerRef} className="alert-sound-banner">
        <span id={messageId} role="alert">
          An alert sound failed to load, so alarms may be silent.
        </span>
        <button
          type="button"
          className="alert-sound-banner-button"
          aria-describedby={messageId}
          onClick={reloadFailedAlertSounds}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!blocked) return null;

  return (
    <div ref={bannerRef} className="alert-sound-banner">
      <span id={messageId} role="alert">
        The browser blocked an alert sound.
      </span>
      <button
        type="button"
        className="alert-sound-banner-button"
        aria-describedby={messageId}
        onClick={retryBlockedAlertSounds}
      >
        Turn on alert sound
      </button>
    </div>
  );
}

export default AlertSoundBanner;
